import {
  addBrandPausedTime,
  AdminCustomProviderSelect,
  AdminDatePickerInputModal,
  api,
  Box,
  Button,
  Disclaimer,
  Div,
  EditPenIconDarkGray,
  ErrorToast,
  H3,
  Icon,
  Img,
  lightTheme,
  Modal,
  P,
  Pill,
  PropsModal,
  Provider,
  renderProviderIcon,
  renderProviderName,
  SuccessToast,
  TooltipWrapper,
  TrashIconRed,
  reportError,
} from '@vgrubs/components';
import {
  Brand,
  BrandPausedTimeDto,
  BrandPausedTimeEntity,
  BrandPausedTimeStatus,
} from 'components/src/types/brand.types';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export const BrandPausedTimesModal = ({
  modalIsOpen,
  closeModal,
  setLoading,
  pausedTimes,
  setAddModalIsOpen,
  setPausedTimes,
  brandId,
  setEditPausedTimeModal,
  timezone,
  setDetails,
}: PropsModal & {
  modalIsOpen: boolean;
  pausedTimes: BrandPausedTimeEntity[];
  setAddModalIsOpen: Dispatch<SetStateAction<boolean>>;
  setPausedTimes: Dispatch<SetStateAction<BrandPausedTimeEntity[]>>;
  brandId: string;
  setEditPausedTimeModal: Dispatch<
    SetStateAction<BrandPausedTimeEntity | null>
  >;
  timezone: string;
  setDetails: Dispatch<SetStateAction<Brand | null>>;
}) => {
  const [deleteModal, setDeleteModal] = useState<
    BrandPausedTimeEntity['id'] | null
  >(null);

  async function handleDeletePausedTime() {
    try {
      setLoading?.(true);
      const { data } = await api.delete(
        `/v1/brands/${brandId}/paused-times/${deleteModal}`
      );
      setPausedTimes((prev) => {
        const filtered = prev.filter((d) => d.id !== deleteModal);
        const hasActivePausedTime = filtered.some(
          (c) => c.status === BrandPausedTimeStatus.active
        );
        setDetails((prev) => {
          if (!prev) return prev;
          return { ...prev, paused: hasActivePausedTime };
        });
        return filtered;
      });
      SuccessToast(data.message);
      setDeleteModal(null);
      if (pausedTimes.length === 1) {
        closeModal?.();
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  const handleAdd = () => {
    closeModal?.();
    setAddModalIsOpen(true);
  };

  return (
    <>
      <Modal
        isOpen={!!modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header="Order Pause Schedule"
        // overflow="visible"
      >
        {pausedTimes?.map((pt, i) => {
          return (
            <Box
              $mb="8"
              key={i}
              $borderColor={pt.status === 'active' && lightTheme.primary}
            >
              <Div $flex $alignCenter $between $gap="16px">
                <Div $flex $column $gap="8px" $alignStart>
                  <P $m="0" $left>
                    <b>From:</b>{' '}
                    {DateTime.fromISO(pt.startDate, { zone: 'utc' })
                      .setZone(timezone)
                      .toFormat('MMM dd, yyyy hh:mm a')}
                  </P>
                  <P $m="0" $left>
                    <b>To:</b>{' '}
                    {DateTime.fromISO(pt.endDate, { zone: 'utc' })
                      .setZone(timezone)
                      .toFormat('MMM dd, yyyy hh:mm a')}
                  </P>
                  {pt.providers.length > 0 && (
                    <Div $flex $alignCenter $gap="8px" $mt="2" $wrap>
                      {pt.providers.map((p) => (
                        <TooltipWrapper tooltip={renderProviderName(p)}>
                          <Img
                            alt=""
                            src={renderProviderIcon(p)}
                            $width="24px"
                            $widthSM="24px"
                          />
                        </TooltipWrapper>
                      ))}
                    </Div>
                  )}
                </Div>
                <Div $flex $alignCenter $between>
                  <Div $flex $alignCenter $gap="12px" $column>
                    <BrandPausedTimeStatusPill status={pt.status} />
                    <Div $flex $alignCenter $gap="8px">
                      {pt.status !== 'expired' && (
                        <Icon
                          $pointer
                          onClick={() => setEditPausedTimeModal(pt)}
                        >
                          <EditPenIconDarkGray />
                        </Icon>
                      )}
                      <Icon $pointer onClick={() => setDeleteModal(pt.id)}>
                        <TrashIconRed />
                      </Icon>
                    </Div>
                  </Div>
                </Div>
              </Div>
              {/* <Separator $m="12px 0" /> */}
            </Box>
          );
        })}

        <Button $selected $w="100%" $mt="32" onClick={handleAdd}>
          Add Order Pause Schedule
        </Button>
      </Modal>

      <DeletePausedTimeModal
        deleteModal={deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        handleDeletePausedTime={handleDeletePausedTime}
      />
    </>
  );
};

export const AddBrandPausedTimesModal = ({
  modalIsOpen,
  closeModal,
  setLoading,
  setPausedTimes,
  setPausedTimeModal,
  edit,
  brandId,
  timezone,
  setDetails,
}: PropsModal & {
  modalIsOpen: BrandPausedTimeEntity | boolean | null;
  setPausedTimes: Dispatch<SetStateAction<BrandPausedTimeEntity[]>>;
  setPausedTimeModal: Dispatch<SetStateAction<boolean>>;
  edit?: boolean;
  brandId: string;
  timezone: string;
  setDetails: Dispatch<SetStateAction<Brand | null>>;
}) => {
  const initialBrandNow = DateTime.now().setZone(timezone ?? 'UTC');
  const initialDate = new Date(
    initialBrandNow.year,
    initialBrandNow.month - 1,
    initialBrandNow.day,
    initialBrandNow.hour,
    initialBrandNow.minute,
    0,
    0
  );

  const [startDate, setStartDate] = useState<Date[]>([]);
  const [endDate, setEndDate] = useState<Date[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);

  async function handleAddBrandPausedTime() {
    if (!modalIsOpen) return;

    if (!providers.length) {
      return ErrorToast('Please select at least one provider.');
    }

    if (!startDate[0] || !endDate[0]) {
      return ErrorToast('Please select both start and end date.');
    }

    const brandZone = timezone ?? 'UTC';

    const buildBrandDate = (date: Date) =>
      DateTime.fromObject(
        {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate(),
          hour: date.getHours(),
          minute: date.getMinutes(),
        },
        { zone: brandZone }
      );

    const startBrand = buildBrandDate(startDate[0]);
    const endBrand = buildBrandDate(endDate[0]);

    const timestampStartDate = startBrand.toSeconds();
    const timestampEndDate = endBrand.toSeconds();

    if (timestampStartDate > timestampEndDate) {
      return ErrorToast(
        `The start date must be earlier than the end date. Please adjust your input and try again.`
      );
    }

    const body: BrandPausedTimeDto = {
      startDate: startBrand.toUTC().toISO() ?? '',
      endDate: endBrand.toUTC().toISO() ?? '',
      providers,
    };

    if (edit && typeof modalIsOpen === 'object') {
      body.id = modalIsOpen.id;
    }

    try {
      setLoading?.(true);
      const { data } = await addBrandPausedTime(brandId, body);

      if (edit) {
        setPausedTimes((prev) => {
          const updated = prev.map((c) =>
            c.id === data.data.id ? data.data : c
          );
          const hasActivePausedTime = updated.some(
            (c) => c.status === BrandPausedTimeStatus.active
          );
          setDetails((prev) => {
            if (!prev) return prev;
            return { ...prev, paused: hasActivePausedTime };
          });

          return updated;
        });
      } else {
        setPausedTimes((prev) => {
          const updated = [...prev, data.data];
          const hasActivePausedTime = updated.some(
            (c) => c.status === BrandPausedTimeStatus.active
          );
          setDetails((prev) => {
            if (!prev) return prev;
            return { ...prev, paused: hasActivePausedTime };
          });
          return updated;
        });
      }

      SuccessToast(data.message);
      closeModal?.();
      setPausedTimeModal(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    if (
      edit &&
      typeof modalIsOpen === 'object' &&
      modalIsOpen &&
      'providers' in modalIsOpen
    ) {
      setProviders(modalIsOpen.providers as Provider[]);
      const pausedTime = modalIsOpen;
      if (!pausedTime) return;
      if (typeof pausedTime !== 'object') return;

      const brandZone = timezone ?? 'UTC';

      const startBrand = DateTime.fromISO(pausedTime.startDate, {
        zone: 'utc',
      }).setZone(brandZone);

      const endBrand = DateTime.fromISO(pausedTime.endDate, {
        zone: 'utc',
      }).setZone(brandZone);

      // ❗️Important: we create a JS Date using only Y/M/D/H/M.
      // We don't care about the real instant; Flatpickr will just show these fields.
      const sd = new Date(
        startBrand.year,
        startBrand.month - 1,
        startBrand.day,
        startBrand.hour,
        startBrand.minute,
        0,
        0
      );

      const ed = new Date(
        endBrand.year,
        endBrand.month - 1,
        endBrand.day,
        endBrand.hour,
        endBrand.minute,
        0,
        0
      );

      setStartDate([sd]);
      setEndDate([ed]);
    } else {
      setStartDate([]);
      setEndDate([]);
      setProviders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);

  const brandNow = DateTime.now().setZone(timezone);

  const minDate = new Date(
    brandNow.year,
    brandNow.month - 1,
    brandNow.day,
    brandNow.hour,
    brandNow.minute
  );

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`${edit ? 'Edit' : 'Add'} Order Pause`}
      overflow="visible"
      modalOverflow="visible"
      width="550px"
      zIndex={110}
    >
      <AdminCustomProviderSelect
        value={providers}
        onChange={(value) => setProviders(value as Provider[])}
        label="Providers"
        placeholder="Select Providers"
        $textCenter
        $shadowDesign={false}
        multiSelect
      />
      <Div $flex $alignCenter $columnSM $gap="16px" $gapSM="16px" $mt="24">
        <Div $width="100%">
          <AdminDatePickerInputModal
            format="MMM dd, yyyy hh:mm a"
            options={{
              enableTime: true,
              dateFormat: 'Y-m-d H:i',
              minDate: minDate,
              defaultDate: initialDate,
              defaultHour: initialBrandNow.hour, // Sets default hour to 9 AM
              defaultMinute: initialBrandNow.minute,
            }}
            placeholder="Pick a date"
            $textCenter
            label="Start Date"
            onChange={setStartDate}
            value={startDate}
          />
        </Div>
        <Div $width="100%">
          <AdminDatePickerInputModal
            format="MMM dd, yyyy hh:mm a"
            options={{
              enableTime: true,
              dateFormat: 'Y-m-d H:i',
              minDate: minDate,
              defaultDate: initialDate,
              defaultHour: initialBrandNow.hour, // Sets default hour to 9 AM
              defaultMinute: initialBrandNow.minute,
            }}
            placeholder="Pick a date"
            $textCenter
            label="End Date"
            onChange={setEndDate}
            value={endDate}
          />
        </Div>
      </Div>

      <Button $selected $w="100%" $mt="32" onClick={handleAddBrandPausedTime}>
        Save
      </Button>
    </Modal>
  );
};

export const BrandPausedTimePill = ({
  pausedTime,
}: {
  pausedTime?: boolean;
}) => {
  if (pausedTime) {
    return (
      <Pill $background={lightTheme.primary}>
        <Disclaimer $m="0" $color={lightTheme.white}>
          Orders Paused
        </Disclaimer>
      </Pill>
    );
  } else return null;
};

export const BrandPausedTimeStatusColor = (status: BrandPausedTimeStatus) => {
  switch (status) {
    case BrandPausedTimeStatus.active:
      return lightTheme.primary;

    case BrandPausedTimeStatus.expired:
      return lightTheme.lightGray;

    case BrandPausedTimeStatus.upcoming:
      return lightTheme.darkGray;

    default:
      return undefined;
  }
};

export const BrandPausedTimeStatusPill = ({ status }) => {
  return (
    <Pill $background={BrandPausedTimeStatusColor(status)}>
      <Disclaimer $m="0" $color={lightTheme.white} $capitalize>
        {status}
      </Disclaimer>
    </Pill>
  );
};

export const DeletePausedTimeModal = ({
  deleteModal,
  closeModal,
  handleDeletePausedTime,
}: PropsModal & {
  deleteModal: BrandPausedTimeEntity['id'] | null;
  handleDeletePausedTime: () => Promise<void>;
}) => {
  return (
    <Modal
      isOpen={!!deleteModal}
      closeModal={closeModal}
      logo={false}
      zIndex={110}
      width="400px"
    >
      <H3 $mb="8"> Are you sure</H3>
      <P $mb="24">Are you sure you want to delete this order pause schedule?</P>

      <Div $flex $gap="16px">
        <Button $selected $w="100%" $mb="8" onClick={handleDeletePausedTime}>
          Confirm
        </Button>

        <Button $selected $w="100%" $gray onClick={() => closeModal?.()}>
          Cancel
        </Button>
      </Div>
    </Modal>
  );
};
